import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Loader2, RefreshCw, Search, ArrowUpDown, Settings, ArrowRight, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const TOP_CLIENTS_COLUMNS = [
    { key: 'client', label: 'Client' },
    { key: 'sales_p1', label: 'CA P1' },
    { key: 'sales_p2', label: 'CA P2' },
    { key: 'profit_p1', label: 'Profit P1' },
    { key: 'profit_p2', label: 'Profit P2' },
    { key: 'profit_pct_p1', label: 'Profit % P1' },
    { key: 'profit_pct_p2', label: 'Profit % P2' },
    { key: 'share_sales_p1', label: 'Pondere CA P1' },
    { key: 'share_sales_p2', label: 'Pondere CA P2' },
    { key: 'share_profit_p1', label: 'Pondere Profit P1' },
    { key: 'share_profit_p2', label: 'Pondere Profit P2' },
    { key: 'articles_p1', label: 'Nr Articole P1' },
    { key: 'articles_p2', label: 'Nr Articole P2' },
    { key: 'growth_sales', label: 'Crestere CA%' },
    { key: 'growth_profit', label: 'Crestere Profit %' },
    { key: 'growth_articles', label: 'Crestere nr articole %' },
];

export default function TopsAnalysis() {
    const [tables, setTables] = useState([]);
    const [comparing, setComparing] = useState(false);

    // Selections
    const [table1, setTable1] = useState('');
    const [table2, setTable2] = useState('');

    // Result Data
    const [result, setResult] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [supplierData, setSupplierData] = useState(null);
    const [topClientsData, setTopClientsData] = useState(null);
    const [visibleTopColumns, setVisibleTopColumns] = useState(
        TOP_CLIENTS_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    );

    const toggleTopColumn = (key) => {
        setVisibleTopColumns(prev => ({ ...prev, [key]: !prev[key] }));
    };


    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${API}/tables`);
            setTables(res.data || []);
        } catch (e) {
            console.error(e);
            toast.error("Nu am putut incarca lista de fisiere.");
        }
    };

    const renderTopClientsTable = (data, title, yearP1, yearP2) => {
        if (!data || data.length === 0) return null;

        return (
            <Card className="bg-[#18181b] border-white/10 overflow-hidden mb-8">
                <div className="bg-yellow-400/90 p-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-black uppercase">
                        {title}
                    </h2>
                    {/* Column Toggle Button */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="bg-black/10 hover:bg-black/20 text-black border-black/20 h-8">
                                <Settings className="mr-2 h-4 w-4" />
                                Coloane
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 bg-[#18181b] border-white/20 text-white p-0">
                            <div className="p-3 border-b border-white/10">
                                <h4 className="font-medium text-sm text-gray-400">Selecteaza Coloane</h4>
                            </div>
                            <div className="p-1 max-h-[300px] overflow-y-auto">
                                {TOP_CLIENTS_COLUMNS.map(col => (
                                    <div key={col.key}
                                        onClick={() => toggleTopColumn(col.key)}
                                        className="flex items-center space-x-2 p-2 hover:bg-white/10 rounded cursor-pointer transition-colors"
                                    >
                                        <div className={`shrink-0 w-4 h-4 border border-white/40 rounded flex items-center justify-center ${visibleTopColumns[col.key] ? 'bg-cyan-600 border-cyan-600' : ''}`}>
                                            {visibleTopColumns[col.key] && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                        <span className="text-sm select-none">{col.label}</span>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[#27272a] border-white/10">
                                {visibleTopColumns.client && <TableHead className="text-gray-200 font-bold border-r border-white/10 min-w-[200px]">Client</TableHead>}
                                {visibleTopColumns.sales_p1 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">CA {yearP1}</TableHead>}
                                {visibleTopColumns.sales_p2 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">CA {yearP2}</TableHead>}
                                {visibleTopColumns.profit_p1 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Profit {yearP1}</TableHead>}
                                {visibleTopColumns.profit_p2 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Profit {yearP2}</TableHead>}
                                {visibleTopColumns.profit_pct_p1 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Profit % {yearP1}</TableHead>}
                                {visibleTopColumns.profit_pct_p2 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Profit % {yearP2}</TableHead>}
                                {visibleTopColumns.share_sales_p1 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Pondere CA {yearP1}</TableHead>}
                                {visibleTopColumns.share_sales_p2 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Pondere CA {yearP2}</TableHead>}
                                {visibleTopColumns.share_profit_p1 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Pondere % profit {yearP1}</TableHead>}
                                {visibleTopColumns.share_profit_p2 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Pondere % profit {yearP2}</TableHead>}
                                {visibleTopColumns.articles_p1 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Nr articole {yearP1}</TableHead>}
                                {visibleTopColumns.articles_p2 && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Nr articole {yearP2}</TableHead>}
                                {visibleTopColumns.growth_sales && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Crestere CA%</TableHead>}
                                {visibleTopColumns.growth_profit && <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Crestere profit %</TableHead>}
                                {visibleTopColumns.growth_articles && <TableHead className="text-gray-200 font-bold text-right">Crestere nr articole %</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.map((row, idx) => (
                                <TableRow key={idx} className="border-white/10 hover:bg-white/5">
                                    {visibleTopColumns.client && <TableCell className="text-white font-bold text-sm py-2 border-r border-white/10">{row.client}</TableCell>}
                                    {visibleTopColumns.sales_p1 && <TableCell className="text-right text-white text-sm py-2 font-mono border-r border-white/10">{formatMoney(row.sales_p1)}</TableCell>}
                                    {visibleTopColumns.sales_p2 && <TableCell className="text-right text-white text-sm py-2 font-mono border-r border-white/10">{formatMoney(row.sales_p2)}</TableCell>}
                                    {visibleTopColumns.profit_p1 && <TableCell className="text-right text-white text-sm py-2 font-mono border-r border-white/10">{formatMoney(row.profit_p1)}</TableCell>}
                                    {visibleTopColumns.profit_p2 && <TableCell className="text-right text-white text-sm py-2 font-mono border-r border-white/10">{formatMoney(row.profit_p2)}</TableCell>}
                                    {visibleTopColumns.profit_pct_p1 && <TableCell className="text-right text-gray-300 text-sm py-2 font-mono border-r border-white/10">{formatPercent(row.profit_pct_p1 * 100)}</TableCell>}
                                    {visibleTopColumns.profit_pct_p2 && <TableCell className="text-right text-gray-300 text-sm py-2 font-mono border-r border-white/10">{formatPercent(row.profit_pct_p2 * 100)}</TableCell>}
                                    {visibleTopColumns.share_sales_p1 && <TableCell className="text-right text-gray-300 text-sm py-2 font-mono border-r border-white/10">{formatPercent(row.share_sales_p1 * 100)}</TableCell>}
                                    {visibleTopColumns.share_sales_p2 && <TableCell className="text-right text-gray-300 text-sm py-2 font-mono border-r border-white/10">{formatPercent(row.share_sales_p2 * 100)}</TableCell>}
                                    {visibleTopColumns.share_profit_p1 && <TableCell className="text-right text-gray-300 text-sm py-2 font-mono border-r border-white/10">{formatPercent(row.share_profit_p1 * 100)}</TableCell>}
                                    {visibleTopColumns.share_profit_p2 && <TableCell className="text-right text-gray-300 text-sm py-2 font-mono border-r border-white/10">{formatPercent(row.share_profit_p2 * 100)}</TableCell>}
                                    {visibleTopColumns.articles_p1 && <TableCell className="text-right text-white text-sm py-2 font-mono border-r border-white/10">{row.articles_p1}</TableCell>}
                                    {visibleTopColumns.articles_p2 && <TableCell className="text-right text-white text-sm py-2 font-mono border-r border-white/10">{row.articles_p2}</TableCell>}
                                    {visibleTopColumns.growth_sales && <TableCell className={`text-right font-bold text-sm py-2 font-mono border-r border-white/10 ${row.growth_sales >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {formatPercent(row.growth_sales * 100)}
                                    </TableCell>}
                                    {visibleTopColumns.growth_profit && <TableCell className={`text-right font-bold text-sm py-2 font-mono border-r border-white/10 ${row.growth_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {formatPercent(row.growth_profit * 100)}
                                    </TableCell>}
                                    {visibleTopColumns.growth_articles && <TableCell className={`text-right font-bold text-sm py-2 font-mono ${row.growth_articles >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {formatPercent(row.growth_articles * 100)}
                                    </TableCell>}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    };

    const handleCompare = async () => {
        if (!table1 || !table2) {
            toast.warning("Te rog selecteaza ambele fisiere pentru comparatie.");
            return;
        }

        setComparing(true);
        setResult(null);
        setClientData(null);
        setClientData(null);

        try {
            // Parallel Fetch
            const [resTotals, resClients, resSup1, resSup2, resTop] = await Promise.all([
                axios.post(`${API}/reports/compare-totals`, { table1, table2 }),
                axios.post(`${API}/reports/compare-clients`, { table1, table2 }),
                axios.post(`${API}/reports/supplier-analysis`, { table_name: table1 }),
                axios.post(`${API}/reports/supplier-analysis`, { table_name: table2 }),
                axios.post(`${API}/reports/top-clients-analysis`, { table1, table2, limit: 5 })
            ]);

            setResult(resTotals.data);
            setClientData(resClients.data);
            setSupplierData({
                p1: { ...resSup1.data, name: table1 },
                p2: { ...resSup2.data, name: table2 }
            });
            setTopClientsData(resTop.data);

            toast.success("Comparatie realizata cu succes!");
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.detail || "Eroare la compararea fisierelor.");
        } finally {
            setComparing(false);
        }
    };



    const formatMoney = (val) => {
        if (val === undefined || val === null) return '-';
        return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatPercent = (val) => {
        if (val === undefined || val === null) return '-';
        return `${Number(val).toFixed(2)}%`;
    };

    const renderSupplierTable = (data) => {
        if (!data || data.error) return null;

        const { total, filtered } = data;

        // Pondere Calculations
        // filtered / total * 100
        const pNr = total.count ? (filtered.count / total.count) * 100 : 0;
        const pSales = total.sales ? (filtered.sales / total.sales) * 100 : 0;
        const pProfit = total.profit ? (filtered.profit / total.profit) * 100 : 0;

        return (
            <Card className="bg-[#18181b] border-white/10 overflow-hidden mb-8">
                <div className="bg-yellow-400/90 p-4">
                    <h2 className="text-xl font-bold text-black uppercase">
                        Numar Furnizori {data.name}
                    </h2>
                </div>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-[#27272a] border-white/10">
                                <TableHead className="text-gray-200 font-bold border-r border-white/10 w-[300px]">Categorie</TableHead>
                                <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">Nr</TableHead>
                                <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">pondere nr clienti</TableHead>
                                <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">CA {data.name}</TableHead>
                                <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">pondere din CA</TableHead>
                                <TableHead className="text-gray-200 font-bold text-right border-r border-white/10">profit {data.name}</TableHead>
                                <TableHead className="text-gray-200 font-bold text-right">pondere din profit</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {/* Row 1: Total */}
                            <TableRow className="border-white/10 hover:bg-white/5">
                                <TableCell className="text-white font-bold text-md py-4 border-r border-white/10">
                                    Total numar furnizori {data.name}
                                </TableCell>
                                <TableCell className="text-right text-white text-md py-4 font-mono border-r border-white/10">
                                    {total.count}
                                </TableCell>
                                <TableCell className="text-right text-gray-400 text-md py-4 font-mono border-r border-white/10">
                                    -
                                </TableCell>
                                <TableCell className="text-right text-white text-md py-4 font-mono border-r border-white/10">
                                    {formatMoney(total.sales)}
                                </TableCell>
                                <TableCell className="text-right text-gray-400 text-md py-4 font-mono border-r border-white/10">
                                    -
                                </TableCell>
                                <TableCell className="text-right text-white text-md py-4 font-mono border-r border-white/10">
                                    {formatMoney(total.profit)}
                                </TableCell>
                                <TableCell className="text-right text-gray-400 text-md py-4 font-mono">
                                    -
                                </TableCell>
                            </TableRow>

                            {/* Row 2: Filtered */}
                            <TableRow className="border-white/10 hover:bg-white/5">
                                <TableCell className="text-white font-bold text-md py-4 border-r border-white/10">
                                    Total furnizori cu un singur client {data.name} si un singur articol
                                </TableCell>
                                <TableCell className="text-right text-white text-md py-4 font-mono border-r border-white/10">
                                    {filtered.count}
                                </TableCell>
                                <TableCell className="text-right text-cyan-400 font-bold text-md py-4 font-mono border-r border-white/10">
                                    {formatPercent(pNr)}
                                </TableCell>
                                <TableCell className="text-right text-white text-md py-4 font-mono border-r border-white/10">
                                    {formatMoney(filtered.sales)}
                                </TableCell>
                                <TableCell className="text-right text-cyan-400 font-bold text-md py-4 font-mono border-r border-white/10">
                                    {formatPercent(pSales)}
                                </TableCell>
                                <TableCell className="text-right text-white text-md py-4 font-mono border-r border-white/10">
                                    {formatMoney(filtered.profit)}
                                </TableCell>
                                <TableCell className="text-right text-cyan-400 font-bold text-md py-4 font-mono">
                                    {formatPercent(pProfit)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    };



    return (
        <div className="p-8 space-y-8 pb-40">
            {/* Header Navigation */}
            <div className="flex items-center space-x-4">
                <Link to="/reports">
                    <Button variant="ghost" className="text-gray-400 hover:text-white">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Inapoi la Rapoarte
                    </Button>
                </Link>
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    TOP's - Analiza Centralizata
                </h1>
            </div>

            {/* Selection Card */}
            <Card className="bg-[#18181b] border-white/10">
                <CardHeader>
                    <CardTitle className="text-gray-300 text-lg">Selectie Perioade de Comparatie</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada Veche (Ex: 2024 / Aprilie)</label>
                        <Select value={table1} onValueChange={setTable1}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white">
                                <SelectValue placeholder="Selecteaza fisier..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada Recenta (Ex: 2025 / Iunie)</label>
                        <Select value={table2} onValueChange={setTable2}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white">
                                <SelectValue placeholder="Selecteaza fisier..." />
                            </SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <Button
                        onClick={handleCompare}
                        disabled={comparing || !table1 || !table2}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                        {comparing ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        Genereaza Comparatie
                    </Button>
                </CardContent>
            </Card>

            {/* Results Section */}
            {result && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

                    {/* TABLE 1: SALES */}
                    <Card className="bg-[#18181b] border-white/10 overflow-hidden">
                        <div className="bg-yellow-400/90 p-4">
                            <h2 className="text-xl font-bold text-black uppercase">
                                Total Vânzări {result.period1.name} vs {result.period2.name}
                            </h2>
                        </div>
                        <CardContent className="p-0">
                            <Table>
                                <TableBody>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">Total {result.period1.name}</TableCell>
                                        <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{formatMoney(result.sales.val1)}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">Total {result.period2.name}</TableCell>
                                        <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{formatMoney(result.sales.val2)}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-white/5 hover:bg-transparent h-4"></TableRow>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">{result.period2.name} - {result.period1.name}</TableCell>
                                        <TableCell className={`text-right font-bold text-lg py-4 font-mono ${result.sales.diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {result.sales.diff >= 0 ? '+' : ''}{formatMoney(result.sales.diff)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">{result.period2.name} - {result.period1.name} procentual</TableCell>
                                        <TableCell className={`text-right font-bold text-lg py-4 font-mono ${result.sales.percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {formatPercent(result.sales.percent)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    {/* TABLE 2: PROFIT */}
                    <Card className="bg-[#18181b] border-white/10 overflow-hidden">
                        <div className="bg-yellow-400/90 p-4">
                            <h2 className="text-xl font-bold text-black uppercase">
                                Total Profit Brut {result.period1.name} vs {result.period2.name}
                            </h2>
                        </div>
                        <CardContent className="p-0">
                            <Table>
                                <TableBody>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">Profit {result.period1.name}</TableCell>
                                        <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{formatMoney(result.profit.val1)}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">Profit {result.period2.name}</TableCell>
                                        <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{formatMoney(result.profit.val2)}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-white/5 hover:bg-transparent h-4"></TableRow>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">{result.period2.name} - {result.period1.name}</TableCell>
                                        <TableCell className={`text-right font-bold text-lg py-4 font-mono ${result.profit.diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {result.profit.diff >= 0 ? '+' : ''}{formatMoney(result.profit.diff)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className="border-white/10 hover:bg-white/5">
                                        <TableCell className="text-gray-300 text-lg py-4">{result.period2.name} - {result.period1.name} procentual</TableCell>
                                        <TableCell className={`text-right font-bold text-lg py-4 font-mono ${result.profit.percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {formatPercent(result.profit.percent)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    {/* TABLE 3: CLIENT EVOLUTION */}
                    {clientData && (
                        <Card className="bg-[#18181b] border-white/10 overflow-hidden">
                            <div className="bg-yellow-400/90 p-4">
                                <h2 className="text-xl font-bold text-black uppercase">
                                    Evoluție nr. clienți activi
                                </h2>
                            </div>
                            <CardContent className="p-0">
                                <Table>
                                    <TableBody>
                                        {/* OLD PERIOD */}
                                        <TableRow className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-gray-300 text-lg py-4 font-bold">Total clienti {clientData.period1.name} (Perioada Veche)</TableCell>
                                            <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{clientData.period1.total_clients}</TableCell>
                                        </TableRow>
                                        <TableRow className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-red-400 text-lg py-4 font-bold">Total clienti pierduti din {clientData.period1.name}</TableCell>
                                            <TableCell className="text-right text-red-400 font-bold text-lg py-4 font-mono">{clientData.period1.lost_count}</TableCell>
                                        </TableRow>
                                        <TableRow className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-gray-300 text-lg py-4">Valoare lei CA clienti pierduti din {clientData.period1.name}</TableCell>
                                            <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{formatMoney(clientData.period1.lost_value)}</TableCell>
                                        </TableRow>

                                        {/* Spacer */}
                                        <TableRow className="border-white/5 hover:bg-transparent h-4"></TableRow>

                                        {/* NEW PERIOD */}
                                        <TableRow className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-gray-300 text-lg py-4 font-bold">Total Clienti {clientData.period2.name} (Perioada Recenta)</TableCell>
                                            <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{clientData.period2.total_clients}</TableCell>
                                        </TableRow>
                                        <TableRow className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-green-400 text-lg py-4 font-bold">Total clienti noi {clientData.period2.name}</TableCell>
                                            <TableCell className="text-right text-green-400 font-bold text-lg py-4 font-mono">{clientData.period2.new_count}</TableCell>
                                        </TableRow>
                                        <TableRow className="border-white/10 hover:bg-white/5">
                                            <TableCell className="text-gray-300 text-lg py-4">Valoare lei CA clienti noi din {clientData.period2.name}</TableCell>
                                            <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">{formatMoney(clientData.period2.new_value)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    {/* SUPPLIER ANALYSIS TABLES */}
                    {supplierData && (
                        <>
                            {renderSupplierTable(supplierData.p1)}
                            {renderSupplierTable(supplierData.p2)}

                            {/* PROFIT PER SUPPLIER TABLE */}
                            <Card className="bg-[#18181b] border-white/10 overflow-hidden mt-8">
                                <div className="bg-yellow-400/90 p-4">
                                    <h2 className="text-xl font-bold text-black uppercase">
                                        Profit / furnizor
                                    </h2>
                                </div>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableBody>
                                            <TableRow className="border-white/10 hover:bg-white/5">
                                                <TableCell className="text-gray-300 text-lg py-4">Total profit vanzari / furnizori {result.period1.name}</TableCell>
                                                <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">
                                                    {formatMoney(supplierData.p1.total.profit)}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow className="border-white/10 hover:bg-white/5">
                                                <TableCell className="text-gray-300 text-lg py-4">Total profit vanzari /furnizori {result.period2.name}</TableCell>
                                                <TableCell className="text-right text-white font-bold text-lg py-4 font-mono">
                                                    {formatMoney(supplierData.p2.total.profit)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        </>
                    )}



                    {/* TOP CLIENTS TABLES */}
                    {/* TOP CLIENTS TABLES */}
                    {topClientsData && (
                        topClientsData.error ? (
                            <div className="bg-red-500/10 border border-red-500/50 p-4 rounded text-red-400">
                                Eroare Top Clienti: {topClientsData.error}
                            </div>
                        ) : (
                            result && (
                                <>
                                    {renderTopClientsTable(topClientsData.top_p1, `Top 5 Clienti ${result.period1.name}`, result.period1.name.split('/')[0], result.period2.name.split('/')[0])}
                                    {renderTopClientsTable(topClientsData.top_p2, `Top 5 Clienti ${result.period2.name}`, result.period1.name.split('/')[0], result.period2.name.split('/')[0])}
                                </>
                            )
                        )
                    )}



                </div >
            )}
        </div >
    );
}
